import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useBiddingDetailController } from './context/bidding-detail-controller';
import { useBiddingDetailState } from './context/bidding-detail-context';
import { formatDate, getBiddingStatusColor, handleViewDocument, handleDownloadDocument } from 'utils';

export function BiddingDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useBiddingDetailController();
  const { bidding, loading, error } = useBiddingDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
    }
  }, [id]);

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={3} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!bidding) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Licitação não encontrada.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Licitação ${bidding.number}`}
          subtitle={bidding.modality || 'Detalhes da licitação'}
          breadcrumbs={[
            { label: 'Licitações', href: '/licitacoes' },
            { label: bidding.number },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <GavelIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {bidding.number}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={bidding.statusDescripton || 'Status não informado'}
                  color={getBiddingStatusColor(bidding.statusDescripton || bidding.status)}
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={bidding.modality || 'Modalidade não informada'}
                  variant="outlined"
                  color="primary"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Objeto da Licitação
                  </Typography>
                  <Typography variant="body1">
                    {bidding.object || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Unidade Gestora
                  </Typography>
                  <Typography variant="body1">
                    {bidding.managementUnit || 'Não informado'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {bidding.processNumber && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Número do Processo
                    </Typography>
                    <Typography variant="body1">
                      {bidding.processNumber}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {bidding.formattedValue && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Valor Estimado
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {bidding.formattedValue}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Datas */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ mr: 1 }} />
            Cronograma
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {bidding.publicatedOn && (
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Data de Publicação
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(bidding.publicatedOn)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {bidding.openedOn && (
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Data de Abertura
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(bidding.openedOn)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {bidding.limitDate && (
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Data Limite
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatDate(bidding.limitDate)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Documentos */}
        {bidding.documents && bidding.documents.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Documentos ({bidding.documents.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {bidding.documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Visualizar" arrow>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleViewDocument(doc.fileViewUrl, doc.originalFileName)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Baixar" arrow>
                        <IconButton
                          edge="end"
                          color="success"
                          onClick={() => handleDownloadDocument(doc.fileDownloadUrl, doc.originalFileName)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'success.main',
                              color: 'white',
                            },
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <DescriptionIcon color="error" sx={{ fontSize: 36 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.documentType || doc.type || 'Não especificado'}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Arquivo: {doc.originalFileName || doc.fileName || 'Não especificado'}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {(!bidding.documents || bidding.documents.length === 0) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum documento disponível para esta licitação.
          </Alert>
        )}

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/licitacoes')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Licitações
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
